import {
  ago,
  type Config,
  defaultSortDir,
  etaDisplay,
  findItem,
  fmtDate,
  fmtDateTime,
  type PeopleMap,
  type QueueSort,
  type QueueTicket,
  type SortDir,
} from '@hrms/shared'
import type { ReactNode } from 'react'
import {
  LuChevronDown,
  LuChevronsUpDown,
  LuChevronUp,
  LuMessageSquare,
  LuPaperclip,
  LuStickyNote,
} from 'react-icons/lu'
import { Avatar } from '../../../components/ui/index.ts'
import { cn } from '../../../lib/cn.ts'
import { hint } from '../../../lib/hint.ts'
import {
  CategoryChip,
  LockIcon,
  PersonAvatar,
  PriorityBars,
  personName,
  StatusLozenge,
} from '../tickets/ui.tsx'

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`

/** ETA cell (DESIGN.md §3.1 "ETA text"). */
function EtaCell({ ticket, config, now }: { ticket: QueueTicket; config: Config; now: number }) {
  const eta = etaDisplay(ticket, { now, config, viewer: 'hr' })
  switch (eta.kind) {
    case 'done':
      return (
        <span
          className="text-muted"
          {...(eta.resolvedAt !== null
            ? hint(`Resolved ${fmtDateTime(eta.resolvedAt, { now })}`)
            : {})}
        >
          Done{eta.resolvedAt !== null && ` ${fmtDate(eta.resolvedAt, { now })}`}
        </span>
      )
    case 'paused':
      return (
        <span className="text-muted" {...hint('The ETA is paused until the employee replies')}>
          Paused
        </span>
      )
    case 'overdue':
      return (
        <span
          className="font-medium text-danger"
          {...hint(
            `Was due ${fmtDateTime(eta.dueAt, { now })}${eta.manual ? ' (set manually)' : ''}`,
          )}
        >
          {eta.label}
        </span>
      )
    case 'due': {
      const source = eta.manual
        ? 'Set manually'
        : eta.priority
          ? `From the ${eta.priority.name} SLA (${eta.priority.slaDays}d)`
          : 'From the SLA'
      return (
        <span {...hint(`${fmtDateTime(eta.dueAt, { now })}. ${source}`)}>
          {fmtDate(eta.dueAt, { now })}
        </span>
      )
    }
    default:
      return <span className="text-faint">—</span>
  }
}

interface Column {
  id: string
  label: string
  sort?: QueueSort
  className?: string
}

const COLUMNS: Column[] = [
  { id: 'key', label: 'Key', sort: 'key', className: 'w-[92px]' },
  { id: 'summary', label: 'Summary', className: 'min-w-[260px] max-w-[420px]' },
  { id: 'category', label: 'Category' },
  { id: 'priority', label: 'Priority', sort: 'priority' },
  { id: 'status', label: 'Status', sort: 'status' },
  { id: 'raisedBy', label: 'Raised by' },
  { id: 'assignee', label: 'Assignee' },
  { id: 'created', label: 'Created', sort: 'created' },
  { id: 'eta', label: 'ETA', sort: 'eta' },
]

const SORT_HINTS: Record<QueueSort, string> = {
  key: 'Sort by ticket number',
  priority: 'Sort by priority, in the order set in Settings',
  status: 'Sort by status, in workflow order',
  created: 'Sort by when the ticket was raised',
  eta: 'Sort by ETA, nearest first. Done tickets always sort last',
}

function HeaderCell({
  col,
  sort,
  dir,
  onSort,
}: {
  col: Column
  sort: QueueSort
  dir: SortDir
  onSort: (sort: QueueSort, dir: SortDir) => void
}) {
  const base =
    'sticky top-0 z-10 border-b border-border bg-surface-2 text-left text-[12.5px] font-medium whitespace-nowrap text-muted'
  if (!col.sort)
    return (
      <th scope="col" className={cn(base, 'px-3 py-[9px]', col.className)}>
        {col.label}
      </th>
    )
  const active = sort === col.sort
  const Icon = !active ? LuChevronsUpDown : dir === 'asc' ? LuChevronUp : LuChevronDown
  const target = col.sort
  return (
    <th
      scope="col"
      aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      className={cn(base, col.className)}
    >
      <button
        type="button"
        onClick={() =>
          onSort(target, active ? (dir === 'asc' ? 'desc' : 'asc') : defaultSortDir(target))
        }
        className={cn(
          'inline-flex w-full items-center gap-1 px-3 py-[9px] hover:text-text',
          active && 'text-text',
        )}
        {...hint(SORT_HINTS[target])}
      >
        {col.label}
        <Icon aria-hidden className={cn('size-3.5', active ? 'text-accent' : 'text-faint')} />
      </button>
    </th>
  )
}

function Person({ people, id }: { people: PeopleMap; id: string }) {
  return (
    <span className="inline-flex max-w-[180px] items-center gap-2">
      <PersonAvatar person={people[id]} id={id} />
      <span className="truncate">{personName(people, id)}</span>
    </span>
  )
}

function Meta({ children, tip }: { children: ReactNode; tip: string }) {
  return (
    <span className="inline-flex items-center gap-1" {...hint(tip)}>
      {children}
    </span>
  )
}

function Row({
  ticket,
  people,
  config,
  now,
  onOpen,
}: {
  ticket: QueueTicket
  people: PeopleMap
  config: Config
  now: number
  onOpen: (key: string) => void
}) {
  const t = ticket
  const notes = t.internalNoteCount
  const priority = findItem(config.priorities, t.priorityId)
  return (
    <tr
      tabIndex={0}
      data-tour="queue-row"
      aria-label={`${t.key}: ${t.title}${t.confidential ? ' (confidential)' : ''}`}
      onClick={() => onOpen(t.key)}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onOpen(t.key)
        }
      }}
      className="cursor-pointer border-b border-border align-top transition-colors last:border-b-0 hover:bg-surface-2 focus-visible:bg-surface-2 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent"
    >
      <td className="tabular px-3 py-2.5 text-[13px] font-semibold whitespace-nowrap text-muted">
        {t.key}
      </td>
      <td className="max-w-[420px] min-w-[260px] px-3 py-2.5">
        <span className="flex items-start gap-1.5 font-medium break-words">
          {t.confidential && <LockIcon className="mt-0.5" />}
          <span className="min-w-0">{t.title}</span>
        </span>
        <span className="mt-0.5 flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-[12.5px] text-faint">
          <Meta tip={`${plural(t.publicCommentCount, 'comment')} the employee can see`}>
            <LuMessageSquare aria-hidden className="size-3.5 text-violet-fg" />
            {t.publicCommentCount}
          </Meta>
          {notes !== null && notes > 0 && (
            <Meta tip="Internal notes are visible to the HR team only">
              <LuStickyNote aria-hidden className="size-3.5 text-amber-fg" />
              {plural(notes, 'internal note')}
            </Meta>
          )}
          {t.attachmentCount > 0 && (
            <Meta tip={plural(t.attachmentCount, 'attachment')}>
              <LuPaperclip aria-hidden className="size-3.5 text-teal-fg" />
              {t.attachmentCount}
            </Meta>
          )}
          <span {...hint(fmtDateTime(t.updatedAt, { now }))}>
            Updated {ago(t.updatedAt, { now })}
          </span>
        </span>
      </td>
      <td className="px-3 py-2.5 whitespace-nowrap">
        <CategoryChip categoryId={t.categoryId} categories={config.categories} />
      </td>
      <td className="px-3 py-2.5 whitespace-nowrap">
        <span
          {...(priority
            ? hint(`${priority.name}: HR aims to resolve within ${priority.slaDays}d`)
            : {})}
        >
          <PriorityBars priorityId={t.priorityId} priorities={config.priorities} />
        </span>
      </td>
      <td className="px-3 py-2.5 whitespace-nowrap">
        <StatusLozenge statusId={t.statusId} statuses={config.statuses} />
      </td>
      <td className="px-3 py-2.5 whitespace-nowrap">
        <Person people={people} id={t.raisedBy} />
      </td>
      <td className="px-3 py-2.5 whitespace-nowrap">
        {t.assignee ? (
          <Person people={people} id={t.assignee} />
        ) : (
          <span className="inline-flex items-center gap-2 text-faint">
            <Avatar />
            Unassigned
          </span>
        )}
      </td>
      <td
        className="px-3 py-2.5 whitespace-nowrap text-muted"
        {...hint(fmtDateTime(t.createdAt, { now }))}
      >
        {fmtDate(t.createdAt, { now })}
      </td>
      <td className="px-3 py-2.5 whitespace-nowrap">
        <EtaCell ticket={t} config={config} now={now} />
      </td>
    </tr>
  )
}

/** Sortable ticket table with a sticky header; scrolls sideways inside its own box. */
export function QueueTable({
  tickets,
  people,
  config,
  now,
  sort,
  dir,
  onSort,
  onOpen,
  loading,
  footer,
}: {
  tickets: QueueTicket[]
  people: PeopleMap
  config: Config
  now: number
  sort: QueueSort
  dir: SortDir
  onSort: (sort: QueueSort, dir: SortDir) => void
  onOpen: (key: string) => void
  loading?: boolean
  footer?: ReactNode
}) {
  return (
    <div className="overflow-hidden rounded-panel border border-border bg-surface">
      <div className="max-h-[calc(100dvh-180px)] min-h-[200px] overflow-auto">
        <table className="w-full min-w-[1040px] border-collapse text-sm" aria-busy={loading}>
          <thead>
            <tr data-tour="queue-sort">
              {COLUMNS.map((c) => (
                <HeaderCell key={c.id} col={c} sort={sort} dir={dir} onSort={onSort} />
              ))}
            </tr>
          </thead>
          <tbody className={cn(loading && 'opacity-60 transition-opacity')}>
            {tickets.map((t) => (
              <Row
                key={t.id}
                ticket={t}
                people={people}
                config={config}
                now={now}
                onOpen={onOpen}
              />
            ))}
          </tbody>
        </table>
      </div>
      {footer}
    </div>
  )
}

/** Placeholder rows while the first page loads. */
export function QueueSkeleton() {
  return (
    <div className="overflow-hidden rounded-panel border border-border bg-surface" aria-busy="true">
      <p className="sr-only" aria-live="polite">
        Loading tickets…
      </p>
      <div className="h-[37px] border-b border-border bg-surface-2" />
      {['a', 'b', 'c', 'd', 'e', 'f'].map((k) => (
        <div
          key={k}
          className="flex items-center gap-4 border-b border-border px-3 py-3.5 last:border-b-0"
        >
          <span className="h-3 w-14 animate-pulse rounded bg-surface-3" />
          <span className="h-3 flex-1 animate-pulse rounded bg-surface-3" />
          <span className="h-3 w-20 animate-pulse rounded bg-surface-3" />
          <span className="h-3 w-16 animate-pulse rounded bg-surface-3" />
          <span className="h-3 w-24 animate-pulse rounded bg-surface-3 max-[720px]:hidden" />
        </div>
      ))}
    </div>
  )
}
